import styled from 'styled-components'
import React, { useState, useContext, useEffect } from 'react'
import { get } from 'lodash'
import { Field } from 'react-final-form'
import {
  TextField,
  InputAdornment,
  IconButton,
  OutlinedInput,
  FormControl,
  InputLabel,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { IntlProvider } from 'react-intl'

import { login } from '../requests/login'
import { useRequest } from '../hooks/request'
import { loginRedirect } from '../utils/loginRedirect'
import { VARIANT_SECONDARY } from '../constants/variants'
import { PATHS } from '../constants/paths'
import { UserContext } from '../data/UserContext'
import { FromContext } from '../data/FromContext'
import { VERSION_APP } from '../constants/baseUrl'

import { rowCenterXY } from './Mixins'
import { Trad } from './common/Trad'
import { Button } from './common/Button'
import { Error } from './common/Error'
import { Link } from './common/Link'
import { Margin } from './common/Margin'
import { Form } from './common/Form'
import { Logo } from './icons/Logo'
import { useNavigate } from 'react-router'

const useStyles = makeStyles((theme) => ({
  '@global': {
    body: {
      backgroundColor: theme.palette.common.white,
    },
  },

  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },

  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },

  form: {
    width: '90%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },

  input: {
    marginBottom: theme.spacing(2),
  },

  margin: {
    margin: theme.spacing(1),
  },

  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  submit: {
    margin: theme.spacing(3, 0, 2),
  },

  textField: {
    width: 200,
  },

  versionDiv: {
    display: 'block',
    position: 'absolute',
    paddingLeft: 5,
    left: 0,
    bottom: 0,
  },
}))

export const Login = () => {
  const navigate = useNavigate()
  const classes = useStyles()
  const [showPassword, setShowPassword] = useState(false)
  const { from, setFrom } = useContext(FromContext)
  const { setUser } = useContext(UserContext)

  useEffect(() => {
    setUser('')
  }, [])
  
  const loginRequest = useRequest({
    request: login,
    handler: (p) => p.then((token) => {
      loginRedirect({ token, path: from, navigate: navigate })
      setFrom('')
    }).catch(() => { }),
  })

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleMouseDownPassword = (e) => {
    e.preventDefault()
  }

  const errorCode = get(loginRequest, 'state.data.errorCode')

  const messages = {
    password: 'Mot de passe',
    connect: 'Se connecter',
    forgotPassword: 'Mot de passe oublié ?',
    'loginRequest.10000': "Ce mot de passe n'est soit plus aux normes, soit pas le bon. Dans les deux cas, vérifiez bien puis utilisez la procédure de mot de passe oublié pour en choisir un nouveau.",
    'loginRequest.11000': 'Identifiants incorrect',
    errorRetry: 'Une erreur s\'est produite, merci de réessayer',
  }

  return (
    <IntlProvider locale="fr" messages={messages}>
      <Wrapper>
        <div className={classes.versionDiv}>
          v
          {VERSION_APP}
        </div>
        <div className={classes.paper}>
          <Logo />
          <Form
            onSubmit={loginRequest.fetch}
            render={({ handleSubmit }) => (
              <form className={classes.form} noValidate onSubmit={handleSubmit}>
                <Field
                  name="email"
                  render={({ input }) => (
                    <TextField
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      id="email"
                      label="Email"
                      name="email"
                      autoComplete="email"
                      className={classes.input}
                      autoFocus
                      {...input}
                    />
                  )}
                />

                <Field
                  name="password"
                  render={({ input }) => (
                    <FormControl fullWidth variant="outlined">
                      <InputLabel htmlFor="outlined-adornment-password">
                        <Trad id="password" />
                      </InputLabel>
                      <OutlinedInput
                        {...input}
                        id="outlined-adornment-password"
                        type={showPassword ? 'text' : 'password'}
                        endAdornment={(
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        )}
                        labelWidth={70}
                      />
                    </FormControl>
                  )}
                />

                <Margin v={20} />
                <ButtonWrapper>
                  <Button
                    type="submit"
                    variant={VARIANT_SECONDARY}
                    filled
                    tradId="connect"
                    loading={loginRequest.state.loading || loginRequest.state.pendingHandler}
                  />
                  {loginRequest.state.error && (
                    <Error>
                      <Trad id={errorCode ? `loginRequest.${errorCode}` : 'errorRetry'} />
                    </Error>
                  )}
                  <Margin v={10} />
                  <Link to={PATHS.FORGOT_PASSWORD}>
                    <Trad id="forgotPassword" />
                  </Link>
                </ButtonWrapper>
              </form>
            )}
          />
        </div>
      </Wrapper>
    </IntlProvider>
  )
}

const Wrapper = styled.div`
  ${rowCenterXY}
  min-height: calc(100vh);
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`
